// shared/utils/modulesConversion.ts
// Put this in your shared package and import from both client & admin.
// Filename suggestion: shared/utils/modulesConversion.ts

import { debug } from "console";

export type PageModuleRaw =
  | string
  | {
      module?: string;      // instance name used as ComponentName
      sort_index?: number; // ordering index
      [k: string]: any;
    };

export type PageModuleObj = {
  module?: string;
  sort_index?: any;
  [k: string]: any;
};

/**
 * Returns true when EVERY element is an object with a 'module' prop.
 * This is a strict check to avoid mis-detecting mixed arrays.
 */
export const isNewModulesFormat = (arr?: PageModuleRaw[]): arr is PageModuleObj[] => {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  return arr.every((item) => typeof item === 'object' && item !== null && Object.prototype.hasOwnProperty.call(item, 'module'));
};
export const insertModuleAtIndex = (modules: PageModuleRaw[] = [], index: number, moduleInstance: string): PageModuleObj[] => {
  const objs = ensureModulesObjects(modules);
  const idx = Math.max(0, Math.min(index, objs.length));
  const arr = [...objs.slice(0, idx), { module: moduleInstance }, ...objs.slice(idx)];
  return arr.map((m, i) => ({ ...m, sort_index: i + 1 }));
};


/**
 * Convert any modules array to array of PageModuleObj.
 * - If already object-format (with optional metadata) returns normalized objects with sequential sort_index.
 * - If it's string[] or mixed, converts strings -> objects and preserves objects, then sets sequential sort_index.
 * - Returns [] for falsy input.
 *
 * Idempotent and defensive.
 */
export const ensureModulesObjects = (modules?: PageModuleRaw[]): PageModuleObj[] => {
  if (!Array.isArray(modules) || modules.length === 0) return [];

  return modules.map((m, idx) => {
    if (typeof m === 'string') {
      // string modules get a default index based on position
      return { module: m, sort_index: idx + 1 };
    }

    // m is object: keep other metadata, but ensure module exists
    const mod = m as PageModuleObj;

    // try to parse an existing sort_index; accept fractional values
    const parsed = Number(mod.sort_index);
    const hasValidSort = Number.isFinite(parsed) && parsed > 0;

    return {
      ...mod,
      module: mod.module ?? String(mod as any) ?? `unknown_${idx}`,
      // keep provided valid sort_index, otherwise fallback to idx + 1
      sort_index: hasValidSort ? parsed : idx + 1,
    };
  });
};


/**
 * Get instance (module) names in the correct render order:
 * - For new-format objects: order by sort_index ascending.
 * - Otherwise assume string[] and return as is.
 */
export const getInstanceNamesFromPageModules = (pageModules?: PageModuleRaw[]): string[] => {
  if (!Array.isArray(pageModules) || pageModules.length === 0) return [];
  if (isNewModulesFormat(pageModules)) {
return (pageModules as PageModuleObj[])
  .slice()
  .sort(
    (a, b) =>
      (a.sort_index ?? Number.MAX_SAFE_INTEGER) -
      (b.sort_index ?? Number.MAX_SAFE_INTEGER)
  )
  .filter((m) => !m.isDeleted) // <-- remove deleted modules entirely
  .map((m) => {
    if(m.overrideModuleName){
      return m.overrideModuleName
    }
    return m.module
  });
  }
  // If mixed or strings, produce names by mapping: strings or object.module
  return pageModules.map((m) => (typeof m === 'string' ? m : (m as PageModuleObj).module));
};

/**
 * Reorder / move element: dragIndex -> hoverIndex.
 * - If the array contains any object (i.e. not pure strings), we treat it as object-format:
 *   convert to objects using ensureModulesObjects, move, then renormalize sort_index 1..n and return objects.
 * - If it's pure string[], we swap/move within strings and return string[].
 *
 * Returns new array (non-mutating).
 */
const safeNum = (v: any): number | number  => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN; // null or NaN ??????
};
export const reorderModulesArray = (
  modules: PageModuleRaw[] = [],
  dragIndex: number,
  hoverIndex: number
): PageModuleRaw[] => {
  if (!Array.isArray(modules) || modules.length === 0) return modules;

  // Normalize to objects but DO NOT force re-sequencing of sort_index here.
  // We'll preserve existing sort_index values if present.
  const objs = (modules || []).map((m: any, i: number) => {
    if (typeof m === 'string') {
      // fallback for legacy string entry (no sort_index available)
      return { module: m, sort_index: null };
    }
    // keep all existing keys but ensure module property exists
    const mod = { ...(m || {}) };
    mod.module = mod.module ?? mod.instance ?? `unknown_${i}`;
    mod.sort_index = mod.sort_index ?? safeNum(mod.sort_index) ?? null;
    return mod;
  });

  // Separate visible (non-deleted) and deleted modules
  const visible = objs.filter((x) => !x?.isDeleted);
  const deleted = objs.filter((x) => !!x?.isDeleted);

  // Sort visible by numeric sort_index (nulls go to end)
  visible.sort((a: any, b: any) => {
    const na = safeNum(a?.sort_index);
    const nb = safeNum(b?.sort_index);
    if (!Number.isFinite(na) && !Number.isFinite(nb)) return 0;
    if (!Number.isFinite(na)) return 1;
    if (!Number.isFinite(nb)) return -1;
    return na - nb;
  });

  // Validate indices against visible array length
  const len = visible.length;
  if (dragIndex < 0 || dragIndex >= len || hoverIndex < 0 || hoverIndex >= len) {
    // invalid indices -> return original array normalized and sorted by sort_index
    const merged = [...visible, ...deleted].sort((a: any, b: any) => {
      const na = safeNum(a?.sort_index);
      const nb = safeNum(b?.sort_index);
      if (!Number.isFinite(na) && !Number.isFinite(nb)) return 0;
      if (!Number.isFinite(na)) return 1;
      if (!Number.isFinite(nb)) return -1;
      return na - nb;
    });
    return merged;
  }

  // Move item inside visible array
  const visibleCopy = [...visible];
  const [item] = visibleCopy.splice(dragIndex, 1);
  visibleCopy.splice(hoverIndex, 0, item);

  // Reassign sort_index for visible modules to sequential integers (1..n)
  // (Client-side simplicity: we renumber visible modules so ordering is deterministic)
  const reindexedVisible = visibleCopy.map((m: any, idx: number) => ({
    ...m,
    sort_index: idx + 1,
  }));

  // Keep deleted modules with their original sort_index intact.
  // Merge and sort all modules by numeric sort_index for final output.
  const merged = [...reindexedVisible, ...deleted].sort((a: any, b: any) => {
    const na = safeNum(a?.sort_index);
    const nb = safeNum(b?.sort_index);
    if (!Number.isFinite(na) && !Number.isFinite(nb)) return 0;
    if (!Number.isFinite(na)) return 1;
    if (!Number.isFinite(nb)) return -1;
    return na - nb;
  });

  return merged;
};
export const getModuleInstanceName = (item: PageModuleRaw): string => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  // new-format objects: module is the instance name (defensive fallback to any known keys)
  return (item.module ?? (item as any).instance ?? '') as string;
};
/**
 * Remove module by instance name from modules array.
 * - Preserves input element shapes: strings stay strings, objects stay objects.
 * - For mixed arrays, it simply filters by matching instance name.
 */
export const removeModuleFromModulesArray = (modules?: PageModuleRaw[], instanceName?: string): PageModuleRaw[] => {
  if (!Array.isArray(modules) || !instanceName) return Array.isArray(modules) ? [...modules] : [];
  return modules.filter((m) => getModuleInstanceName(m) !== instanceName);
};

/**
 * Swap two indexes (keeps same semantics as previous swapIndexes).
 * - For object-format (or mixed), returns PageModuleObj[] with renormalized sort_index.
 * - For string arrays, returns string[] with swapped elements.
 *
 * Returns new array (non-mutating).
 */
export const swapIndexes = (arr: PageModuleRaw[] = [], index1: any, index2: any): PageModuleRaw[] => {
  // 
  if (!Array.isArray(arr) || index1 < 0 || index1 >= arr.length || index2 < 0 || index2 >= arr.length) {
    throw new Error('Invalid indexes or input array');
  }

  const containsObjects = arr.some((m) => typeof m !== 'string');

  if (containsObjects) {
    const objs = ensureModulesObjects(arr);
    const res = [...objs];
    [res[index1], res[index2]] = [res[index2], res[index1]];
    return res.map((m, idx) => ({ ...m, sort_index: idx + 1 }));
  }

  const res = [...(arr as string[])];
  [res[index1], res[index2]] = [res[index2], res[index1]];
  return res;
};

export default {
  isNewModulesFormat,
  ensureModulesObjects,
  getInstanceNamesFromPageModules,
  getModuleInstanceName,
  removeModuleFromModulesArray,
  reorderModulesArray,
  swapIndexes,
  insertModuleAtIndex
};
